import * as React from 'react';
import { twMerge } from 'tailwind-merge';

import { CalendarIcon } from '../../assets/icons/index';
import { useFormat } from '../../hooks/index';
import { Calendar } from '../calendar/index';
import { Popover, PopoverContent, PopoverTrigger } from '../popover/index';

export type DatePickerProps = {
  children?: React.ReactNode;
  className?: string;
  defaultMonth?: Date;
  disabled?: boolean;
  /** Days the calendar must refuse — react-day-picker matcher, e.g. `{ before: approvalDate }`. */
  disabledDates?: React.ComponentProps<typeof Calendar>['disabled'];
  onSelect?: (state: Date) => void;
  value?: Date | null;
};

export const DatePicker: React.FC<DatePickerProps> = ({
  onSelect,
  className,
  children,
  defaultMonth,
  disabled,
  disabledDates,
  value,
}) => {
  const [internalDate, setInternalDate] = React.useState<Date | undefined>(new Date());
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const { format } = useFormat();

  const isControlled = value !== undefined;
  const date = isControlled ? (value ?? undefined) : internalDate;

  const handleDatePicked = (state: Date | undefined) => {
    state && onSelect?.(state);
    if (!isControlled) setInternalDate(state);
    setIsPopoverOpen(false);
  };

  return (
    <Popover onOpenChange={setIsPopoverOpen} open={isPopoverOpen}>
      <PopoverTrigger
        className={twMerge(
          'flex w-full cursor-pointer items-center justify-center gap-1 px-3 py-[6px] font-normal',
          !date && 'text-muted-foreground',
          className,
        )}
        disabled={disabled}
      >
        {children || (
          <>
            {date ? (
              <span className="text-sm font-[450] text-gray-700">{format(date, 'dd/MM/yyyy')}</span>
            ) : (
              <span className="text-sm font-[450] text-gray-700">Date</span>
            )}
            <CalendarIcon />
          </>
        )}
      </PopoverTrigger>
      <PopoverContent className="z-[100] w-auto p-0">
        <Calendar
          defaultMonth={defaultMonth}
          disabled={disabledDates}
          initialFocus
          mode="single"
          onSelect={handleDatePicked}
          selected={date}
        />
      </PopoverContent>
    </Popover>
  );
};
